<template>
    <v-container fluid>
        <ConfirmDialog
            :is-open="deleteAttachmentDialog"
            :data="currentAttachment"
            :action-loading="deleteAttachmentLoading"
            removal
            @on-confirm="deleteComplaintAttachment(currentAttachment.id)"
            @on-abort="closeConfirmDeleteAttachmentDialog"
        >
            <template #title>
                Usuwanie załącznika
            </template>
            <template #content>
                Czy na pewno chcesz usunąć załącznik: <strong>{{ getFileName(currentAttachment) }}</strong>?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-overlay
            color="white"
            opacity="0.85"
            absolute
            :value="fileProgress.show"
        >
            <v-container class="pa-0">
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            :rotate="-90"
                            :size="120"
                            :width="4"
                            :value="fileProgress.value"
                            color="primary"
                        >
                            {{ fileProgress.value }}
                        </v-progress-circular>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <span class="text-caption primary--text">{{ fileProgress.fileName }}</span>
                </v-row>
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-btn
                            color="error"
                            small
                            outlined
                            data-sentry-label="Anuluj"
                            @click="abortUploadAttachments(fileProgress.fileName)"
                        >
                            Anuluj
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-overlay>
        <v-row>
            <v-col cols="auto" sm="8" md="6" lg="8">
                <v-file-input
                    v-model="currentFiles"
                    label="Pliki"
                    multiple
                    counter
                    placeholder="Wybierz pliki"
                    prepend-icon="mdi-paperclip"
                    outlined
                    dense
                    :show-size="1000"
                    :error-messages="currentComplaintAttachmentsErrors"
                    :disabled="isTabReadOnly || isReadOnly"
                >
                    <template #selection="{text}">
                        <v-chip label small>
                            {{ text }}
                        </v-chip>
                    </template>
                </v-file-input>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    outlined
                    :disabled="isTabReadOnly || currentFiles.length === 0"
                    data-sentry-label="Dodaj"
                    @click="uploadAttachments"
                >
                    <v-icon left>
                        mdi-file-upload
                    </v-icon>
                    Dodaj
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="auto" sm="12" md="6" lg="8">
                <v-data-table
                    v-if="complaint.complaintAttachment.length > 0"
                    :headers="headers"
                    :items="complaint.complaintAttachment"
                    :mobile-breakpoint="0"
                    disable-pagination
                    hide-default-footer
                >
                    <template #item.fileName="{item}">
                        <v-tooltip top :open-delay="tooltipOpenDelay" :close-delay="tooltipCloseDelay">
                            <template #activator="{on,attrs}">
                                <span
                                    v-bind="attrs"
                                    class="pointer"
                                    v-on="on"
                                    @click="openInNewTab(item)"
                                >{{ getFileName(item) }}</span>
                            </template>
                            <span>Kliknij żeby pobrać</span>
                        </v-tooltip>
                    </template>
                    <template #item.actions="{item}">
                        <v-tooltip top :open-delay="tooltipOpenDelay" :close-delay="tooltipCloseDelay">
                            <template #activator="{on,attrs}">
                                <span v-bind="attrs" v-on="on">
                                    <v-btn
                                        icon
                                        :disabled="isTabReadOnly || isReadOnly"
                                        @click="showDeleteAttachmentDialog(item)"
                                    >
                                        <v-icon color="error">mdi-delete</v-icon>
                                    </v-btn>
                                </span>
                            </template>
                            <span>Usuń</span>
                        </v-tooltip>
                    </template>
                </v-data-table>
                <v-alert
                    v-else
                    type="info"
                    text
                    border="left"
                    class="mt-4"
                >
                    Nie dodano załączników.
                </v-alert>

                <v-alert
                    border="left"
                    color="blue lighten-4"
                    colored-border
                    icon="mdi-information-outline"
                    elevation="0"
                    class="mt-4"
                >
                    Dopuszczalny rozmiar pojedynczego pliku: <strong>{{ maxFileSizeMB }} MB</strong>
                </v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { appConfig, apiConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import { errorHandler } from '@/utils/errorHandler'
import ConfirmDialog from '@/components/common/ConfirmDialog'

const maxFileSize = (value) => {
    return value.size < appConfig.complaints.complaintAttachmentSize * 1024 * 1024
}

/* eslint-disable vue/no-mutating-props */
export default {
    name: 'AttachmentsTab',
    components: {
        ConfirmDialog
    },
    mixins: [
        tooltipDelay, parse
    ],
    props: {
        complaint: {
            type: Object,
            default: null
        },
        uploadedFiles: {
            type: Array,
            default: () => []
        },
        canSaveComplaint: {
            type: Boolean
        },
        storeModuleName: {
            type: String,
            default: ''
        },
        isReadOnly: {
            type: Boolean,
        }
    },
    data() {
        return {
            currentFiles: [],
            headers: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            deleteAttachmentDialog: false,
            deleteAttachmentLoading: false,
            currentAttachment: {},
        }
    },
    validations() {
        const validations = {}

        if (this.currentFiles.length > 0) {
            validations['currentFiles'] = {
                $each: {
                    maxFileSize
                }
            }
        }

        return validations
    },
    computed: {
        fileProgress() {
            return this.$store.state.common.progress
        },
        userId() {
            return this.$store.state.user.userId
        },
        isDraftComplaint() {
            return this.complaint.complaintStatusId === 15 // robocza
        },
        isComplaintIneditable() {
            return this.isDraftComplaint && (this.complaint.creator.id != this.userId)
        },
        isTabReadOnly() {
            return this.isComplaintIneditable
        },
        currentComplaintAttachmentsErrors() {
            if (this.currentFiles.length === 0) return

            const errors = []

            if (!this.$v.currentFiles?.$dirty) return errors
            this.$v.currentFiles.$invalid && errors.push(`Przekroczono dopuszczalny rozmiar pliku: ${appConfig.complaints.complaintAttachmentSize}MB.`)

            return errors
        },
        maxFileSizeMB() {
            return appConfig.complaints.complaintAttachmentSize
        }
    },
    methods: {
        getFileName(attachment) {
            if (attachment.file !== null && attachment.file !== undefined) {
                return this.parseFileName(attachment.file)
            }

            return ''
        },
        uploadAttachments() {
            if (this.isTabReadOnly) return

            this.$v.currentFiles?.$touch()

            if (this.$v.currentFiles?.$invalid) return

            const uploadSingleFile = () => {
                if (this.currentFiles.length > 0) {

                    const formData = new FormData()

                    formData.append('file', this.currentFiles[0])

                    this.$store.dispatch(`${this.storeModuleName}/createAttachment`, {
                        formData: formData,
                        fileName: Object.fromEntries(formData).file.name
                    })
                        .then(response => {
                            this.complaint.complaintAttachment.push(response.data)
                            this.currentFiles.shift()
                            uploadSingleFile()
                        })
                        .catch(error => {
                            if (error.message !== 'canceled') {
                                errorHandler(error)
                            }
                        })
                }
            }

            uploadSingleFile()
        },
        abortUploadAttachments(fileName) {
            if (this.isTabReadOnly) return

            this.$store.dispatch(`${this.storeModuleName}/createAttachment`, {
                formData: null,
                fileName: fileName
            })
                .catch(error => {
                    if (error.message !== 'canceled') {
                        errorHandler(error)
                    }
                })
        },
        deleteComplaintAttachment(id) {
            if (this.isTabReadOnly) return

            this.deleteAttachmentLoading = true
            this.$store.dispatch(`${this.storeModuleName}/deleteAttachment`, id)
                .then(() => {
                    const idx = this.complaint.complaintAttachment.findIndex(attachment => attachment.id === id)

                    if (idx > -1) {
                        this.complaint.complaintAttachment.splice(idx, 1)
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmDeleteAttachmentDialog()
                })
        },
        showDeleteAttachmentDialog(attachment) {
            if (this.isTabReadOnly) return

            this.currentAttachment = attachment
            this.deleteAttachmentDialog = true
        },
        closeConfirmDeleteAttachmentDialog() {
            this.deleteAttachmentDialog = false
            this.deleteAttachmentLoading = false
            this.currentAttachment = {}
        },
        openInNewTab(file) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            window.open(`${mediaRoot}${file.file}`, '_blank').focus()
        },
    }
}
</script>
